import * as React from 'react';
import { useIntl } from 'react-intl';
import Chart from 'chart.js';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useRoute } from 'wouter';
// import zoomPlugin from 'chartjs-plugin-zoom';
import ChartComponent from '../../Chart';
import { URL_GRAPHS, URL_THROTTLE } from '../../../router/constants';
import {
  renderGraphTooltip,
  plugin,
  IGraphData,
  getMinutesGraphHeight,
  IGraphCode,
  getClickedPointForCompressedGraph,
  getClickedPoint,
  IGraphXYData,
  IGraphDataset,
  GRAPH_POINT_BORDER_COLOR,
  GRAPH_SELECTED_POINT_COLOR,
  GRAPH_BORDER_COLOR,
  GRAPH_COMPR_BACKGROUND_COLOR,
  GRAPH_COMPR_BORDER_COLOR,
} from '../GraphService';
import { THROTTLE } from '../../OnlinePanel/Widgets/ThrottleWidget';
import DynamicLeftAxis from '../DynamicLeftAxis';
import { lastClickedTimestampAtom, tooltipCodeAtom } from '../GraphState';
import { isMenuShownAtom } from '../../MainMenu/MainMenuState';

export const RED_TYPE = '#b50536';
export const GREEN_TYPE = '#1C957A';
export const ORANGE_TYPE = '#df731d';

const RED_BACKGROUND = 'rgb(181, 5, 54, 0.5)';
const GREEN_BACKGROUND = 'rgba(5, 181, 144, 0.5)';
const ORANGE_BACKGROUND = 'rgb(233, 115, 29, 0.5)';

interface IProps {
  graphData: IGraphData;
  noAnimation?: string;
  isCompressed: boolean;
  isSelectable?: boolean;
  isOnline?: boolean;
}

export default function VelocityChart(props: IProps) {
  const { formatMessage: f } = useIntl();
  const lastClickedTimestamp = useRecoilValue<string>(lastClickedTimestampAtom);
  // const isLoaderActive = useRecoilValue<boolean>(AppState.setLoader);
  const [, params] = useRoute(`${URL_GRAPHS}${URL_THROTTLE}/:vehicleId`);
  const mainMenuToggle = useRecoilValue<boolean>(isMenuShownAtom);
  const [graphWidthDiff, setGraphWidthDiff] = React.useState<number>(-15);
  const [graphWidthAfterResize, setGraphWidthAfterResize] = React.useState<null | number>(null);
  const setTooltipCode = useSetRecoilState<IGraphCode | null>(tooltipCodeAtom);

  if (Chart && Chart.defaults && Chart.defaults.global && Chart.defaults.global.plugins) {
    Chart.defaults.global.plugins.gridBgColor = {
      backgroundColorRepeat: false,
      backgroundColor: 'rgba(127,127,127,0.0)',
    };
  }
  if (plugin) {
    Chart.plugins.register({ ...plugin });
  }

  const handleGraphInitialized = (event) => {
    if (event.target.id === 'toggleMenu') {
      setGraphWidthDiff(!mainMenuToggle ? 163 : -163);
      if (graphWidthAfterResize) {
        setGraphWidthAfterResize(null);
      }
    }
  };

  const handleGraphResize = () => {
    const canvas = document.getElementById('componentChart');
    if (canvas) {
      setGraphWidthAfterResize(canvas.clientWidth);
    }
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleGraphResize);
    window.addEventListener('click', handleGraphInitialized);
    return () => {
      window.removeEventListener('resize', handleGraphResize);
      window.addEventListener('click', handleGraphInitialized);
    };
  });

  const getColorString = React.useCallback((value?: number | null) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (value >= 70) {
      return RED_TYPE;
    }
    if (value >= 40) {
      return ORANGE_TYPE;
    }
    return GREEN_TYPE;
  }, []);

  const handleWheelOutside = React.useCallback(() => {
    const tooltipEl = document.getElementById('chartjs-tooltip');
    if (tooltipEl) {
      tooltipEl.style.opacity = '0';
    }
  }, []);

  const handleEscape = React.useCallback((event) => {
    if (event.key === 'Escape') {
      const tooltipEl = document.getElementById('chartjs-tooltip');
      if (tooltipEl) {
        tooltipEl.style.opacity = '0';
      }
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('wheel', handleWheelOutside, false);
    document.addEventListener('keydown', handleEscape, false);
    return () => {
      document.addEventListener('wheel', handleWheelOutside, false);
      document.addEventListener('keydown', handleEscape, false);
    };
  }, [handleEscape, handleWheelOutside]);

  const getBackgroundColor = (type: string) => {
    if (type === RED_TYPE) {
      return RED_BACKGROUND;
    }
    if (type === ORANGE_TYPE) {
      return ORANGE_BACKGROUND;
    }
    return GREEN_BACKGROUND;
  };

  const generateDatasets = React.useCallback(() => {
    if (props.graphData && props.graphData.yLabels && props.graphData.yLabels.length > 0) {
      const xData = props.graphData.xLabels;
      const yData = props.graphData.yLabels;
      const datasets: IGraphDataset[] = [];
      let xyData: IGraphXYData[] = [];

      Object.keys(yData).forEach((key) => {
        const index = Number(key);
        xyData.push({ x: xData[index], y: yData[index] });
        const currentColorType = getColorString(yData[index]);
        const nextColorType = getColorString(yData[index + 1]);

        if (currentColorType && currentColorType !== nextColorType) {
          const datasetsData =
            index + 1 < yData.length
              ? [...xyData, { x: xData[index + 1], y: yData[index + 1] }]
              : xyData;
          const clickedPoint = getClickedPoint(datasetsData, lastClickedTimestamp);
          datasets.push({
            data: datasetsData,
            backgroundColor: getBackgroundColor(currentColorType),
            borderColor: currentColorType,
            borderWidth: 2,
            pointBackgroundColor: GRAPH_SELECTED_POINT_COLOR,
            pointBorderColor: GRAPH_POINT_BORDER_COLOR,
            pointRadius: clickedPoint,
            pointHoverRadius: clickedPoint,
            cubicInterpolationMode: 'monotone',
          });
          xyData = [];
        }
      });
      return datasets;
    }
    return [];
  }, [getColorString, lastClickedTimestamp, props.graphData]);

  const getDatasets = React.useCallback(() => {
    return props.isCompressed
      ? [
          {
            label: '',
            // lineTension: 0,
            data: props.graphData.yLabels,
            backgroundColor: '',
            borderColor: GRAPH_BORDER_COLOR,
            borderWidth: 2,
            pointBackgroundColor: GRAPH_SELECTED_POINT_COLOR,
            pointBorderColor: GRAPH_POINT_BORDER_COLOR,
            pointRadius: getClickedPointForCompressedGraph(
              lastClickedTimestamp,
              props.graphData.xLabels
            ),
            pointHoverRadius: getClickedPointForCompressedGraph(
              lastClickedTimestamp,
              props.graphData.xLabels
            ),
            cubicInterpolationMode: 'monotone',
            fill: 0,
          },
          {
            label: '',
            // lineTension: 0,
            data: props.graphData.minLabels,
            backgroundColor: GRAPH_COMPR_BACKGROUND_COLOR,
            borderColor: GRAPH_COMPR_BORDER_COLOR,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            cubicInterpolationMode: 'monotone',
            fill: 0,
          },
          {
            label: '',
            // lineTension: 0,
            data: props.graphData.maxLabels,
            backgroundColor: GRAPH_COMPR_BACKGROUND_COLOR,
            borderColor: GRAPH_COMPR_BORDER_COLOR,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            cubicInterpolationMode: 'monotone',
            fill: 0,
          },
        ]
      : generateDatasets();
  }, [generateDatasets, lastClickedTimestamp, props.graphData, props.isCompressed]);

  const graphOptions = {
    horizontalLine: [
      {
        y: 40,
        style: GREEN_TYPE,
      },
      {
        y: 70,
        style: ORANGE_TYPE,
      },
      {
        y: 104.99,
        style: '#d9d9d9',
      },
    ],
    legend: {
      display: false,
      labels: {
        fontColor: '#555',
      },
    },
    tooltips: {
      enabled: false,
      intersect: false,

      custom(tooltipModel) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const code = renderGraphTooltip(
          tooltipModel,
          this,
          props.graphData.tooltips,
          THROTTLE,
          '%',
          f,
          props.isCompressed
        );
        setTooltipCode(code);
      },
    },
    // plugins: {
    //   zoom: {
    //     pan: {
    //       enabled: true,
    //       mode: 'x',
    //     },
    //     zoom: {
    //       enabled: true,
    //       mode: 'x',
    //     },
    //   },
    // },
    maintainAspectRatio: false,
    aspectRatio: 4,
    scales: {
      yAxes: [
        {
          display: true,
          ticks: {
            beginAtZero: true,
            fontColor: 'transparent',
            steps: 10,
            stepValue: 10,
            stepSize: 10,
            max: 105,
          },
          afterTickToLabelConversion(scaleInstance) {
            if (scaleInstance && scaleInstance.ticks && scaleInstance.ticks[0]) {
              scaleInstance.ticks[0] = null;
            }
          },
          gridLines: {
            // zeroLineColor: 'green',
            // color: '#ccc',
            // backgroundColor: ['lightgrey', 'white'],
            // backgroundColorRepeat: true,
          },
        },
      ],
      xAxes: [
        {
          display: true,
          type: 'time',
          time: {
            unit: 'minute',
            unitStepSize: 1,
            displayFormats: {
              second: 'HH:mm:ss',
              minute: 'HH:mm',
              hour: 'HH',
            },
          },
          // stacked: false,
          // scaleLabel: {
          //   display: true,
          //   labelString: 'X axe name',
          //   fontColor: '#b50536',
          //   fontSize: 10,
          // },
          // type: 'time',
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            maxRotation: 0,
            minRotation: 0,
          },
          // ticks: {
          //   // fontColor: 'green',
          //   // fontSize: 14,
          // },
          gridLines: {
            // zeroLineColor: 'green',
            color: '#e6e6e6',
            circular: true,
            backgroundColor: ['#f2f2f2', '#fff'],
            backgroundColorRepeat: true,
          },
        },
      ],
    },
    [props.noAnimation || 'undefined']: false,
  };

  return (
    <div style={{ position: 'relative' }}>
      <DynamicLeftAxis
        isDetail={!!params}
        colors={[RED_TYPE, ORANGE_TYPE, GREEN_TYPE, GREEN_TYPE]}
        colorBoundaries={[100, 70, 40, 0]}
        maxValue={105}
        detailValues={!!params}
      />
      {props.graphData.yLabels.length > 0 ? (
        [
          <ChartComponent
            data={{
              // labels: props.isCompressed ? props.graphData.xLabels : undefined,
              labels: props.graphData.xLabels,
              datasets: getDatasets(),
            }}
            options={graphOptions}
            // type="multicolorLine"
            height={getMinutesGraphHeight(props.graphData, !!params)}
            isSelectable={props.isSelectable}
            isOnline={props.isOnline}
          />,
          <div
            style={{
              position: 'absolute',
              top: '0px',
              right: '0px',
              width: '1px',
              height: params ? '378px' : '170px',
              background: '#d9d9d9',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: '0px',
                right: '0px',
                width: '30px',
                height: '1px',
                background: '#a6a6a6',
              }}
            />
          </div>,
        ]
      ) : (
        <div style={{ height: params ? '408px' : '200px' }}>
          {/* {!isLoaderActive && (
            <div
              className="col-12 col-md-8 mx-auto fs-7 text-gray-light"
              style={{ top: params ? '195px' : '55px' }}
            >
              {f({ id: 'graph.onlinepanel.noData' })}
            </div>
          )} */}
        </div>
      )}
    </div>
  );
}
